import React, { useEffect, useMemo, useState } from "react";

import { MessageSquare } from "lucide-react";
import moment from "moment";

import { formatDate, formatThaiMonth } from "react-lib/utils/dateUtils";

import { ButtonV4, ChipV4, SelectV4 } from "../common/ui";
import ModalCreateAppointmentV4, { type SavedAppointment } from "./ModalCreateAppointmentV4";

import {
  ALL,
  DAY_HEAD,
  MAKE_ACTIONS,
  MAKE_SEQUENCE,
  blocksFromDsb,
  blocksOn,
  cellsOf,
  dateKeyOf,
  itemsOf,
  panelDateText,
  providerOptions,
  rangeTitle,
  sameDay,
  shiftAnchor,
  toDate,
  type Block,
  type MakeAppointmentSlice,
  type ViewType,
} from "./appointmentModelV4";
import type { AppointmentRunSequence, ItemSearchEvent } from "./CardAppointmentV4";

type AppointmentCalendarV4Props = {
  make?: MakeAppointmentSlice | null;
  searchedItemListWithKey?: Record<string, unknown>;
  runSequence: AppointmentRunSequence;
  onEvent?: (event: ItemSearchEvent) => void;
  onResult: (text: string) => void;
  onModalOpen: () => void;
  onCreated?: (id: number | string) => void;
};

const VIEWS: { key: ViewType; label: string }[] = [
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
  { key: "day", label: "Day" },
];

const AppointmentCalendarV4 = (props: AppointmentCalendarV4Props) => {
  const [picked, setPicked] = useState<Date>(new Date());
  const [blockId, setBlockId] = useState<number | null>(null);

  const anchor = toDate(props.make?.currentDate);
  const view: ViewType = props.make?.viewType ?? "month";
  const providerId = props.make?.selectedProviderIdsList?.[0] ?? null;
  // ตารางยังไม่มา (restart ล้าง dsb)
  const loading = !props.make?.dsb && !props.make?.showError;

  // Memo
  const blocks = useMemo(
    () => blocksFromDsb(props.make?.dsb, props.make?.allProviders),
    [props.make?.dsb, props.make?.allProviders],
  );

  const dayBlocks = useMemo(
    () => blocksOn(blocks, dateKeyOf(picked), providerId),
    [blocks, picked, providerId],
  );

  const pickedBlock = useMemo(
    () => dayBlocks.find((block) => block.dsbId === blockId) ?? null,
    [dayBlocks, blockId],
  );

  const dayItems = useMemo(
    () => itemsOf(pickedBlock ? [pickedBlock] : dayBlocks),
    [pickedBlock, dayBlocks],
  );

  // Effect
  useEffect(() => {
    // โหลดตารางใหม่เมื่อกลับมาแท็บนี้
    if (props.make?.dsb) {
      props.runSequence({
        sequence: MAKE_SEQUENCE,
        action: MAKE_ACTIONS.BATCH_DL,
        currentDate: anchor,
      });
    }
  }, []);

  useEffect(() => {
    setBlockId(null);
  }, [providerId]);

  // Handler
  const handleMoveTo = (date: Date) => {
    props.runSequence({
      sequence: MAKE_SEQUENCE,
      action: MAKE_ACTIONS.SET_CURRENT_DATE,
      currentDate: date,
    });

    setPicked(date);
    setBlockId(null);
  };

  const handlePickDate = (date: Date) => {
    setPicked(date);
    setBlockId(null);
  };

  const handleToggleBlock = (id: number) => {
    setBlockId(id === blockId ? null : id);
  };

  const handleChangeView = (viewType: ViewType) => {
    props.runSequence({ sequence: MAKE_SEQUENCE, action: MAKE_ACTIONS.SET_VIEW_TYPE, viewType });
  };

  const handleChangeProvider = (value: string) => {
    props.runSequence({
      sequence: MAKE_SEQUENCE,
      action: MAKE_ACTIONS.SET_PROVIDER_FILTER,
      selectedProviderIdsList: value === ALL ? [] : [Number(value)],
    });
  };

  const handleOpenCreate = () => {
    props.onModalOpen();

    props.runSequence({
      sequence: MAKE_SEQUENCE,
      action: MAKE_ACTIONS.SET_SHOW_CREATE_MODAL,
      showCreateModal: true,
    });
  };

  const handleCloseCreate = () => {
    if (props.make?.showError) {
      props.runSequence({ sequence: MAKE_SEQUENCE, action: MAKE_ACTIONS.CLOSE_ERROR_MODAL });
    }

    props.runSequence({
      sequence: MAKE_SEQUENCE,
      action: MAKE_ACTIONS.SET_SHOW_CREATE_MODAL,
      showCreateModal: false,
    });
  };

  const handleSaved = (saved: SavedAppointment) => {
    props.runSequence({
      sequence: MAKE_SEQUENCE,
      action: MAKE_ACTIONS.SET_SHOW_CREATE_MODAL,
      showCreateModal: false,
    });

    handleMoveTo(saved.date);

    if (saved.id) {
      props.onCreated?.(saved.id);
    }

    props.onResult(
      `สร้างนัดหมาย ${saved.id ?? "-"} · ${saved.name} · ${formatDate(saved.date)} ${saved.time}`,
    );
  };

  // Render
  const renderCellLabel = (date: Date): React.ReactNode => {
    const day = moment(date).date();
    const text = view === "month" ? day : `${day} ${formatThaiMonth(date)}`;
    return sameDay(date, new Date()) ? <span className="hv4-appt-today">{text}</span> : text;
  };

  const renderBlockChip = (block: Block) => (
    <span
      key={block.dsbId}
      className={`hv4-appt-blk ${block.full ? "hv4-appt-full" : `hv4-appt-prov-${block.colorIndex}`}`}
    >
      {block.items.length > 0 && <span className="hv4-appt-dot" aria-label="มีนัด" />}
      {block.start} - {block.end} {block.provider}
      {block.full && <span className="hv4-appt-full-tag">เต็ม</span>}
    </span>
  );

  return (
    <>
      <section className="hv4-appt-box hv4-appt-bar" aria-label="ควบคุมปฏิทิน">
        <div className="hv4-appt-seg" role="group" aria-label="มุมมอง">
          {VIEWS.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={view === item.key}
              onClick={() => handleChangeView(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <ButtonV4 variant="ghost" onClick={() => handleMoveTo(new Date())}>
          วันนี้
        </ButtonV4>
        <ButtonV4
          variant="ghost"
          className="hv4-appt-nav"
          aria-label="ก่อนหน้า"
          onClick={() => handleMoveTo(shiftAnchor(view, anchor, -1))}
        >
          ‹
        </ButtonV4>
        <ButtonV4
          variant="ghost"
          className="hv4-appt-nav"
          aria-label="ถัดไป"
          onClick={() => handleMoveTo(shiftAnchor(view, anchor, 1))}
        >
          ›
        </ButtonV4>
        <span className="hv4-appt-range">{rangeTitle(view, anchor)}</span>
        <div className="hv4-appt-prov">
          <label className="hv4-label" htmlFor="hv4-appt-cal-provider">
            ผู้ให้บริการ
          </label>
          <SelectV4
            id="hv4-appt-cal-provider"
            options={providerOptions(props.make?.allProviders, true)}
            value={providerId === null ? ALL : String(providerId)}
            onChange={handleChangeProvider}
          />
        </div>
      </section>

      <div className="hv4-appt-cal-wrap">
        <section className="hv4-appt-box hv4-appt-cal" aria-label="ปฏิทินนัดหมาย">
          <div className={`hv4-appt-days${view === "day" ? " hv4-appt-days--day" : ""}`}>
            {view !== "day" &&
              DAY_HEAD.map((name) => (
                <div key={name} className="hv4-appt-dayhead">
                  {name}
                </div>
              ))}
            {cellsOf(view, anchor).map((date) => {
              const key = dateKeyOf(date);
              const cellBlocks = blocksOn(blocks, key, providerId);
              const classes = [
                "hv4-appt-cell",
                view !== "month" && `hv4-appt-cell--${view}`,
                view === "month" && !moment(date).isSame(anchor, "month") && "hv4-appt-cell--out",
                sameDay(date, picked) && "hv4-appt-cell--picked",
              ];
              return (
                <div
                  key={key}
                  role="button"
                  tabIndex={0}
                  aria-label={formatDate(date)}
                  aria-pressed={sameDay(date, picked)}
                  className={classes.filter(Boolean).join(" ")}
                  onClick={() => handlePickDate(date)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handlePickDate(date);
                    }
                  }}
                >
                  <span className="hv4-appt-num">{renderCellLabel(date)}</span>
                  <div className="hv4-appt-blocks">{cellBlocks.map(renderBlockChip)}</div>
                  {view === "day" &&
                    itemsOf(cellBlocks).map((item) => (
                      <div
                        key={item.id}
                        className={`hv4-appt-dayitem${item.insertQueue ? " hv4-appt-dayitem--insert" : ""}`}
                      >
                        {item.time.replace(":", ".")} {item.shortName}
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        </section>

        <aside className="hv4-appt-box hv4-appt-panel" aria-label="นัดหมายของวันที่เลือก">
          <div className="hv4-appt-panel-title">{panelDateText(picked)}</div>
          {dayBlocks.length ? (
            <div className="hv4-appt-pblks">
              {dayBlocks.map((block) => (
                <button
                  key={block.dsbId}
                  type="button"
                  aria-pressed={block.dsbId === blockId}
                  className={`hv4-appt-pblk ${
                    block.full ? "hv4-appt-full" : `hv4-appt-prov-${block.colorIndex}`
                  }${block.dsbId === blockId ? " hv4-appt-pblk--on" : ""}`}
                  onClick={() => handleToggleBlock(block.dsbId)}
                >
                  {block.items.length > 0 && <span className="hv4-appt-dot" aria-hidden="true" />}
                  <span className="hv4-appt-pblk-name">
                    [{block.start} - {block.end}] {block.provider}
                  </span>
                  {block.full ? (
                    <span className="hv4-appt-tag hv4-appt-tag--full">เต็ม</span>
                  ) : (
                    block.items.length > 0 && (
                      <span className="hv4-appt-tag">{block.items.length} นัด</span>
                    )
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="hv4-appt-note">
              {loading ? "กำลังโหลด" : "ไม่มีผู้ให้บริการเปิดรับนัดในวันนี้"}
            </div>
          )}
          <ButtonV4 className="hv4-appt-create" onClick={handleOpenCreate}>
            + สร้างนัดหมาย
          </ButtonV4>
          <div className="hv4-appt-list-head">
            {pickedBlock
              ? `นัดหมาย · ${pickedBlock.provider} (${dayItems.length})`
              : `นัดหมายของวันนี้ (${dayItems.length})`}
          </div>
          {dayItems.length ? (
            dayItems.map((item) => (
              <div key={item.id} className="hv4-appt-apt">
                <MessageSquare size={22} aria-hidden="true" />
                <div className="hv4-appt-apt-main">
                  <div>
                    <b>{item.time}</b> · {item.name}
                  </div>
                  <div className="hv4-appt-sub">
                    {item.insertQueue && "คิวแทรก · "}
                    {item.provider}
                  </div>
                </div>
                <ChipV4 tone={item.statusTone} className="hv4-appt-chip">
                  {item.statusText}
                </ChipV4>
              </div>
            ))
          ) : (
            <div className="hv4-appt-note">{loading ? "กำลังโหลด" : "ยังไม่มีนัดหมายในวันนี้"}</div>
          )}
        </aside>
      </div>

      {!!props.make?.showCreateModal && (
        <ModalCreateAppointmentV4
          make={props.make}
          blocks={blocks}
          date={picked}
          providerId={pickedBlock?.providerId ?? providerId ?? dayBlocks[0]?.providerId ?? null}
          searchedItemListWithKey={props.searchedItemListWithKey}
          runSequence={props.runSequence}
          onEvent={props.onEvent}
          onClose={handleCloseCreate}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

AppointmentCalendarV4.displayName = "AppointmentCalendarV4";

export default AppointmentCalendarV4;
